import { createFileRoute } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { lazy, Suspense, useEffect, useState } from "react";
import { Loader2, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
import { fetchFaq } from "@/lib/content";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { TopicalAuthorityFaq } from "@/components/site/TopicalAuthorityFaq";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { useWatermarkEnabled } from "@/hooks/use-watermark-settings";
import { webPageScript } from "@/lib/webpage-jsonld";
import { jsonLdScript } from "@/lib/jsonld";

const FaqWatermarks = lazy(() => import("@/components/site/FaqWatermarks"));
const FaqPopup = lazy(() => import("@/components/site/FaqPopup"));

export const Route = createFileRoute("/faq")({
  loader: async () => {
    const faqs = await fetchFaq();
    return { faqs };
  },
  head: ({ loaderData }) => {
    const loaded: any[] = ((loaderData as any)?.faqs ?? []).slice(0, 20);
    const fallback = [
      { question: "How do I file VAT return online in Bangladesh?", answer: "You can file your VAT return (Mushak 9.1) online through Online Tax VAT. Our NBR-certified experts prepare, reconcile and submit your monthly VAT return digitally — no office visits required." },
      { question: "How do I file my income tax return online?", answer: "Submit your income tax return online with Online Tax VAT. We collect your documents digitally, compute tax, and file the return with NBR on your behalf, sending you the official acknowledgement." },
      { question: "How can I get a Trade License online in Bangladesh?", answer: "We process new Trade License applications and renewals online for DSCC, DNCC and other city corporations across Bangladesh — fully digital, with status updates on your dashboard." },
      { question: "How do I get a TIN Certificate (e-TIN) online?", answer: "We complete your e-TIN registration with NBR online and deliver the TIN Certificate to you digitally, usually within 1–2 working days." },
      { question: "How do I register for VAT (BIN) online?", answer: "Online VAT registration (BIN) is done through the NBR portal. Our team prepares your documents, files the application and delivers your BIN certificate online." },
    ];
    const faqs = loaded.length > 0 ? loaded : fallback;
    const scripts: any[] = [
      jsonLdScript({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
      }),
      webPageScript({
        path: "/faq",
        name: "FAQ — Online Tax VAT Bangladesh",
        description: "Answers to common questions about income tax returns, VAT registration, VAT return filing, TIN certificates and trade license support.",
      }),
    ];
    return {
      meta: [
        { title: "FAQ — Tax, VAT & TIN | Online Tax VAT Bangladesh" },
        { name: "description", content: "Answers to common questions about income tax returns, VAT registration, VAT return filing, TIN certificates and trade license support." },
        { property: "og:title", content: "FAQ — Tax, VAT & TIN | Online Tax VAT Bangladesh" },
        { property: "og:description", content: "Answers to common tax, VAT, TIN and trade license questions in Bangladesh." },
        { property: "og:url", content: "https://onlinetaxvat.com/faq" },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "FAQ — Tax, VAT & TIN | Online Tax VAT Bangladesh" },
        { name: "twitter:description", content: "Answers to common tax, VAT and TIN questions in Bangladesh." },
      ],
      links: [{ rel: "canonical", href: "https://onlinetaxvat.com/faq" }],
      scripts,
    };
  },
  component: FAQPage,
});

function FAQPage() {
  const { t, i18n } = useTranslation();
  const i18nLang = i18n.resolvedLanguage || i18n.language || "en";
  const initial = (Route.useLoaderData() as any)?.faqs ?? [];
  const [rows, setRows] = useState<any[]>(initial);
  const [loading, setLoading] = useState(initial.length === 0);
  const [openFaq, setOpenFaq] = useState<any | null>(null);
  const [deferReady, setDeferReady] = useState(false);
  const showWatermark = useWatermarkEnabled("faq");

  useEffect(() => {
    if (initial.length === 0) fetchFaq().then((d) => { setRows(d); setLoading(false); });
    const w = window as any;
    const cb = () => setDeferReady(true);
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(cb, { timeout: 2000 });
    } else {
      setTimeout(cb, 1200);
    }
  }, []);

  useEffect(() => {
    if (!openFaq) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenFaq(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openFaq]);

  return (
    <SiteShell>
      <section
        className="relative pt-14 md:pt-20 pb-20 overflow-hidden"
        style={{ contain: "layout style" }}
      >
        {showWatermark && deferReady && (
          <Suspense fallback={null}>
            <FaqWatermarks />
          </Suspense>
        )}

        <div className="container-page max-w-4xl relative z-10">
          <h1 className="sr-only">Tax &amp; VAT FAQ — Bangladesh</h1>
          <SectionHeading
            eyebrow={t("faqPage.eyebrow")}
            title={<>{t("faqPage.titleA")}<span className="gradient-text">{t("faqPage.titleB")}</span></>}
            description={t("faqPage.desc")}
          />
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="mt-12 glass rounded-3xl p-4 md:p-6 shadow-card border border-border/60">
              <ul className="divide-y divide-border/60">
                {rows.map((f, idx) => (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(f)}
                      className="w-full text-left flex items-start gap-4 py-4 px-2 md:px-4 hover:bg-primary/5 rounded-xl transition-colors group"
                    >
                      <span className="shrink-0 h-9 w-9 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-primary font-bold text-sm">
                        {idx + 1}
                      </span>
                      <span className="flex-1 font-display font-semibold text-base md:text-lg leading-relaxed group-hover:text-primary transition-colors">
                        {f.question}
                      </span>
                      <HelpCircle className="shrink-0 h-5 w-5 text-muted-foreground group-hover:text-primary mt-1.5 transition-colors" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(() => {
            const c = SEO_CONTENT["faq"];
            const isBn = ((Route as any) && false) || (typeof window !== "undefined" && (document.documentElement.lang || "").startsWith("bn"));
            const bn = {
              title: "আমাদের ক্লায়েন্টরা প্রায়শই যে সব প্রশ্ন জিজ্ঞাসা করেন",
              intro:
                "বাংলাদেশে ব্যক্তি ও ব্যবসার জন্য ট্যাক্স, ভ্যাট, বিন (BIN), টিন (TIN) এবং কমপ্লায়েন্স সংক্রান্ত সাধারণ প্রশ্ন ও উত্তর নিচে দেওয়া হলো।",
              bullets: [
                "ব্যক্তিগত আয়কর — স্ল্যাব, রেয়াত এবং ডেডলাইন গাইড",
                "ভ্যাট কমপ্লায়েন্স — নিবন্ধনের সীমা, রিটার্ন এবং অডিট",
                "কোম্পানি ফাইলিং — বার্ষিক রিটার্ন, উইথহোল্ডিং এবং টিডিএস (TDS)",
                "ভিসা ট্যাক্স ফাইল — অ্যাম্বাসির প্রয়োজনীয়তা এবং প্রসেসিং টাইম",
                "রিফান্ড, সারচার্জ এবং এনবিআর (NBR) আদেশের বিরুদ্ধে আপিল",
              ],
              faqs: [
                { q: "আমি যদি আয়কর রিটার্ন দাখিলের শেষ সময় (Deadline) মিস করি তাহলে কী হবে?", a: "ডেডলাইন মিস করলে আইন অনুযায়ী বিলম্ব ফি বা জরিমানা এবং প্রতি মাসের জন্য অতিরিক্ত ২% হারে সারচার্জ বা সুদ যুক্ত হতে পারে। তবে যুক্তিসঙ্গত কারণ দেখিয়ে আপনি সময়ের আগে উপ-কর কমিশনারের কাছে সময় বৃদ্ধির আবেদন করতে পারেন।" },
                { q: "ফ্রিল্যান্সারদের কি ভ্যাট (VAT) রেজিস্ট্রেশন করার প্রয়োজন আছে?", a: "যদি আপনার ফ্রিল্যান্সিং সেবাটি শতভাগ রপ্তানি বা আউটসোর্সিং ক্যাটাগরির হয় এবং আপনি ব্যাংকিং চ্যানেলের মাধ্যমে বৈদেশিক মুদ্রা (রেমিট্যান্স) দেশে নিয়ে আসেন, তবে সাধারণত ভ্যাট অব্যাহতি বা জিরো-রেটেড সুবিধা পাবেন। তবে টার্নওভার নির্দিষ্ট সীমা অতিক্রম করলে বা প্রাতিষ্ঠানিক প্রয়োজনে বিন (BIN) লাগতে পারে।" },
                { q: "আমি কি বাংলাদেশের বাইরে (বিদেশ) থেকে রিটার্ন ফাইল করতে পারব?", a: "হ্যাঁ, অবশ্যই পারবেন! আমাদের সম্পূর্ণ ডিজিটাল ও ক্লাউড-বেসড প্ল্যাটফর্মের মাধ্যমে আপনি বিশ্বের যেকোনো প্রান্ত থেকে আপনার ডকুমেন্ট আপলোড করে এবং ট্রানজেকশন আইডি দিয়ে আমাদের সার্টিফাইড উপদেষ্টাদের মাধ্যমে এনবিআর পোর্টালে সফলভাবে রিটার্ন দাখিল করতে পারবেন।" },
                { q: "দাখিলকৃত বা ফাইল করা রিটার্নে কোনো ভুল হলে তা কীভাবে সংশোধন করব?", a: "রিটার্ন দাখিলের পর কোনো ভুল চোখে পড়লে আয়কর আইন অনুযায়ী আপনি একটি 'সংশোধিত রিটার্ন' বা Revised Return সাবমিট করতে পারেন। আমাদের প্যাকেজে ছোটখাটো সংশোধনের জন্য ফ্রি রি-সাবমিশন সুবিধা অন্তর্ভুক্ত রয়েছে।" },
                { q: "টিন (TIN) এবং বিন (BIN) এর মধ্যে মূল পার্থক্য কী?", a: "TIN (Taxpayer Identification Number) হলো কর শনাক্তকরণ নম্বর যা মূলত ব্যক্তিগত বা প্রাতিষ্ঠানিক আয়কর প্রদানের জন্য ব্যবহৃত হয়। আর BIN (Business Identification Number) হলো ভ্যাট নিবন্ধন নম্বর যা ব্যবসা পরিচালনা, পণ্য আমদানি-রপ্তানি এবং ভ্যাট আদায়ের জন্য বাধ্যতামূলক।" },
                { q: "আমি আমার হারিয়ে যাওয়া টিন (TIN) সার্টিফিকেট কীভাবে পুনরুদ্ধার করব?", a: "আপনার জাতীয় পরিচয়পত্র (NID), নিবন্ধিত মোবাইল নম্বর এবং কিছু সিকিউরিটি তথ্য ব্যবহার করে এনবিআর-এর অফিশিয়াল পোর্টাল থেকে টিন সার্টিফিকেট পুনরুদ্ধার করা সম্ভব। সমস্যা হলে আমাদের কেস অফিসার আপনাকে সরাসরি সাহায্য করবেন।" },
                { q: "বিক্রি বা সেলস শূন্য (Zero Sales) হলেও কি প্রতি মাসে ভ্যাট রিটার্ন (Mushak 9.1) দেওয়া বাধ্যতামূলক?", a: "হ্যাঁ, আপনার ব্যবসার নামে যদি একটি সক্রিয় ভ্যাট বা বিন (BIN) নম্বর থাকে, তবে কোনো বিক্রি না হলেও প্রতি মাসের ১৫ তারিখের মধ্যে এনবিআর-এ 'নিল রিটার্ন' (Nil Return) বা শূন্য রিটার্ন দাখিল করা আইনত বাধ্যতামূলক।" },
                { q: "আয়কর রিটার্ন কেন অনলাইনে জমা দেবেন?", a: "অনলাইনে আয়কর রিটার্ন (e-Return) জমা দিলে আপনি ঘরে বসেই দ্রুত, নিরাপদ এবং কাগজবিহীন উপায়ে NBR-এ রিটার্ন দাখিল করতে পারেন। সিস্টেম স্বয়ংক্রিয়ভাবে কর হিসাব করে, সাথে সাথে রিটার্ন প্রাপ্তি স্বীকার (Acknowledgement Slip) পাওয়া যায় এবং পরবর্তীতে যেকোনো সময় হিস্ট্রি, রিফান্ড স্ট্যাটাস ও পূর্ববর্তী রিটার্ন ডাউনলোড করা যায়। ব্যাংক লোন, ভিসা আবেদন ও টেন্ডারের জন্য এই ই-রিটার্ন কপি গ্রহণযোগ্য প্রমাণ হিসেবে ব্যবহৃত হয়।" },
                { q: "ভ্যাট রেজিস্ট্রেশন (BIN) করতে কী কী লাগে?", a: "অনলাইন VAT রেজিস্ট্রেশন (BIN) এর জন্য প্রয়োজন: মালিকের জাতীয় পরিচয়পত্র (NID), e-TIN সার্টিফিকেট, ট্রেড লাইসেন্স, ব্যাংক সলভেন্সি/স্টেটমেন্ট, ব্যবসা প্রাঙ্গণের ঠিকানা ও ভাড়ার চুক্তিপত্র, এবং অংশীদারি/লিমিটেড কোম্পানির ক্ষেত্রে Partnership Deed বা MOA/AOA ও RJSC সনদ। সব ডকুমেন্ট স্ক্যান করে NBR-এর VAT Online সিস্টেমে আপলোড করতে হয় এবং ৩-৭ কর্মদিবসের মধ্যে ১৩-ডিজিটের BIN ইস্যু হয়।" },
                { q: "২০২৬ সালের ট্যাক্স ফাইল করার নিয়ম কী?", a: "২০২৫-২০২৬ করবর্ষের জন্য ব্যক্তিগত করদাতাদের ৩০ নভেম্বর ২০২৬ এর মধ্যে আয়কর রিটার্ন দাখিল করতে হবে। বার্ষিক আয় ৳৪,০০,০০০-এর বেশি হলে রিটার্ন বাধ্যতামূলক। অনলাইনে eReturn.nbr.gov.bd-তে লগইন করে আয়, বিনিয়োগ ভাতা, উৎসে কর কর্তন (TDS) ও সম্পদ বিবরণী পূরণ করে সাবমিট করুন; নির্ধারিত কর A-Challan বা MFS-এর মাধ্যমে পরিশোধ করা যায়।" },
                { q: "TIN সার্টিফিকেট অনলাইনে কীভাবে নেওয়া যায়?", a: "e-TIN সার্টিফিকেট পেতে secure.incometax.gov.bd-তে গিয়ে NID/পাসপোর্ট দিয়ে অ্যাকাউন্ট খুলুন, ব্যক্তিগত ও ঠিকানার তথ্য পূরণ করুন এবং ক্যাটাগরি (ব্যক্তি/কোম্পানি/অংশীদারি) নির্বাচন করুন। সঠিক তথ্য সাবমিট করলে তাৎক্ষণিকভাবে ১২-ডিজিটের TIN নম্বর সহ সার্টিফিকেট পিডিএফ ফরম্যাটে ডাউনলোড করা যায়—কোনো ফি লাগে না।" },
                { q: "Online Tax VAT Bangladesh-এর সার্ভিস কেন বেছে নেবেন?", a: "আমরা NBR-অনুমোদিত পদ্ধতিতে অভিজ্ঞ ট্যাক্স কনসালটেন্ট দিয়ে ইনকাম ট্যাক্স রিটার্ন, VAT রেজিস্ট্রেশন (BIN), মাসিক VAT রিটার্ন, TIN সার্টিফিকেট, ট্রেড লাইসেন্স ও কোম্পানি ট্যাক্স ফাইলিং সেবা দিচ্ছি। ১০০% অনলাইন প্রক্রিয়া, ডকুমেন্ট আপলোডের পর ২৪-৪৮ ঘণ্টার মধ্যে ডেলিভারি, লাইভ চ্যাট সাপোর্ট, স্বচ্ছ প্যাকেজ মূল্য এবং পেমেন্টের জন্য bKash/Nagad/কার্ড—সব মিলিয়ে বাংলাদেশের সবচেয়ে সহজ ও বিশ্বাসযোগ্য অনলাইন ট্যাক্স প্ল্যাটফর্ম।" },
                { q: "ফ্রিল্যান্সার ও রেমিট্যান্স আয়ের ক্ষেত্রে কর কীভাবে দিতে হবে?", a: "বাংলাদেশের ফ্রিল্যান্সাররা বৈধ চ্যানেলে (Wise, Payoneer, Bank) রেমিট্যান্স আনলে IT/ITES খাতের আয় হিসেবে ২০৩০ সাল পর্যন্ত আয়কর অব্যাহতি (Tax Exemption) পান, তবে রিটার্ন দাখিল বাধ্যতামূলক। ফ্রিল্যান্সিং আয় দেখাতে BEFTN/Bank Credit Advice, প্ল্যাটফর্ম ইনভয়েস ও Remittance Certificate সংরক্ষণ করুন এবং রিটার্নে Exempted Income সেকশনে অন্তর্ভুক্ত করুন।" },
              ],
            };
            const useBn = (i18nLang || "").startsWith("bn");
            const data = useBn ? bn : c;
            return <SeoFaqBlock title={data.title} intro={data.intro} bullets={data.bullets} faqs={data.faqs} />;
          })()}
          <TopicalAuthorityFaq />
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["faq"]} />
        </div>
      </section>

      {(openFaq || deferReady) && (
        <Suspense fallback={null}>
          <FaqPopup openFaq={openFaq} onClose={() => setOpenFaq(null)} />
        </Suspense>
      )}
      <HiddenSeoContext topic="faq" />
    </SiteShell>
  );
}
